import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { adminApi, AuditLog } from '../utils/api';
import { formatDistanceToNow } from 'date-fns';

const AuditLogs: React.FC = () => {
  const [actionFilter, setActionFilter] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [daysFilter, setDaysFilter] = useState<number>(7);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  // Fetch audit logs with filtering
  const { data: auditLogs, isLoading, error } = useQuery<AuditLog[]>({
    queryKey: ['admin-audit-logs', { actionFilter, adminFilter, daysFilter, currentPage }],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        days: daysFilter,
      };
      if (actionFilter) params.action_filter = actionFilter;
      if (adminFilter) params.admin_filter = adminFilter;

      const response = await adminApi.getAuditLogs(params);
      return response.data;
    },
  });

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  const formatAbsoluteDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getSuccessBadge = (success: boolean) => {
    if (success) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-4 h-4 mr-1" />
          Success
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <XCircleIcon className="w-4 h-4 mr-1" />
        Failed
      </span>
    );
  };

  const getActionBadge = (action: string) => {
    const actionStyles: Record<string, string> = {
      login: 'bg-blue-100 text-blue-800',
      logout: 'bg-gray-100 text-gray-800',
      user_status_change: 'bg-yellow-100 text-yellow-800',
      session_terminate: 'bg-orange-100 text-orange-800',
      admin_create: 'bg-purple-100 text-purple-800',
      email_retry: 'bg-indigo-100 text-indigo-800',
      email_bulk_retry: 'bg-indigo-100 text-indigo-800',
    };

    const style = actionStyles[action] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-md ${style}`}>
        {action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    );
  };

  const toggleExpanded = (logId: string) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  };

  const handleResetFilters = () => {
    setActionFilter('');
    setAdminFilter('');
    setDaysFilter(7);
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
          <XCircleIcon className="h-8 w-8 text-red-600" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Audit Logs</h3>
        <p className="text-red-600">
          {error instanceof Error ? error.message : 'Unknown error occurred'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Audit Logs
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            View system activity and administrative actions
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <span className="text-sm text-gray-700">
            Showing {auditLogs?.length || 0} logs
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="action-search" className="block text-sm font-medium text-gray-700 mb-2">
              Search by Action
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="action-search"
                className="admin-input pl-10"
                placeholder="e.g., login, user_status_change..."
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label htmlFor="admin-filter" className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Admin ID
            </label>
            <input
              type="text"
              id="admin-filter"
              className="admin-input"
              placeholder="Admin ID..."
              value={adminFilter}
              onChange={(e) => setAdminFilter(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="days-filter" className="block text-sm font-medium text-gray-700 mb-2">
              Time Range
            </label>
            <select
              id="days-filter"
              className="admin-input"
              value={daysFilter}
              onChange={(e) => setDaysFilter(Number(e.target.value))}
            >
              <option value="1">Last 24 hours</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
        </div>
        {(actionFilter || adminFilter) && (
          <div className="mt-4">
            <button
              onClick={handleResetFilters}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        {auditLogs && auditLogs.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No audit logs found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your filters or time range.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Resource
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IP Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {auditLogs?.map((log) => (
                    <React.Fragment key={log.id}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpanded(log.id)}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900" title={formatAbsoluteDate(log.timestamp)}>
                            {formatDate(log.timestamp)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getActionBadge(log.action)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {log.resource_type && (
                              <span className="font-medium">{log.resource_type}</span>
                            )}
                            {log.resource_id && (
                              <div className="text-xs text-gray-500 font-mono truncate max-w-xs">
                                {log.resource_id}
                              </div>
                            )}
                            {!log.resource_type && !log.resource_id && (
                              <span className="text-gray-400">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 font-mono">
                            {log.ip_address}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getSuccessBadge(log.success)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(log.id);
                            }}
                            className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                          >
                            {expandedLogId === log.id ? (
                              <>
                                Hide <ChevronUpIcon className="ml-1 h-4 w-4" />
                              </>
                            ) : (
                              <>
                                Show <ChevronDownIcon className="ml-1 h-4 w-4" />
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                      {expandedLogId === log.id && (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 bg-gray-50">
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                                    Admin ID
                                  </h4>
                                  <p className="text-sm text-gray-900 font-mono">{log.admin_id || 'N/A'}</p>
                                </div>
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                                    Timestamp (Full)
                                  </h4>
                                  <p className="text-sm text-gray-900">{formatAbsoluteDate(log.timestamp)}</p>
                                </div>
                              </div>
                              {log.details && (
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                                    Details
                                  </h4>
                                  <p className="text-sm text-gray-900 bg-white p-3 rounded border border-gray-200">
                                    {log.details}
                                  </p>
                                </div>
                              )}
                              {log.user_agent && (
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                                    User Agent
                                  </h4>
                                  <p className="text-sm text-gray-900 font-mono bg-white p-3 rounded border border-gray-200 break-all">
                                    {log.user_agent}
                                  </p>
                                </div>
                              )}
                              {log.error_message && (
                                <div>
                                  <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-1">
                                    Error Message
                                  </h4>
                                  <p className="text-sm text-red-900 bg-red-50 p-3 rounded border border-red-200">
                                    {log.error_message}
                                  </p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!auditLogs || auditLogs.length < pageSize}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span>
                    {auditLogs && auditLogs.length < pageSize && (
                      <span> (last page)</span>
                    )}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronUpIcon className="h-5 w-5 transform -rotate-90" aria-hidden="true" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!auditLogs || auditLogs.length < pageSize}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronUpIcon className="h-5 w-5 transform rotate-90" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
